import { useEffect, useContext, useReducer, useState } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { ISLEntryResponse } from "../components/SLCreate/SLCreateSchemas";
import { ISLBatchResponse } from "../components/SLBatch/SLBatchSchemas";
import { INotificationMessagesState } from "../components/NotificationMessages";
import { SLLoadingStatus } from "../utilities/utilities";
import { RequireAtLeastOne } from "../services/TypeUtil";
import { LoggingContext } from "../contexts/LoggingContext";
import { FatalError } from "../services/ErrorHelpers";
import { UserContext, IUserProviderState } from "../contexts/UserContext";
import { getEntryFromApi, sanitizeRegularEntryFromApi } from "../components/SLCreate/SLCreateGetAndSanitization";
import { redirectMeToTheOrigin } from "../services/UrlHelpers";
import { getBatchEntryFromApi, sanitizeBatchEntryFromApi } from "../components/SLBatch/SLBatchGetAndSanitization";

export interface UseSlEntryFromApiState {
  entry: ISLEntryResponse | ISLBatchResponse | null;
  notification: {
    type: keyof INotificationMessagesState;
    messages: string[];
  } | null;
  loadingStatus: SLLoadingStatus;
}

export interface UseDataFromSubledgerState {
  entry: ISLEntryResponse | ISLBatchResponse | null;
  notification: {
    type: keyof INotificationMessagesState;
    messages: string[];
  } | null;
  loadingStatus: SLLoadingStatus;
  dataType: "SLCreate" | "SLBatch";
}

const UseSlEntryInitialState: UseSlEntryFromApiState = {
  entry: null,
  notification: null,
  loadingStatus: SLLoadingStatus.Idle
};

type useSlEntryFromApiStateReducer = (
  state: UseSlEntryFromApiState,
  changes: RequireAtLeastOne<UseSlEntryFromApiState>
) => UseSlEntryFromApiState;

function getEntryId(pageType: "SLCreate" | "SLBatch", urlParams: any): number | string | null {
  if (pageType === "SLCreate") {
    if ("slEntryId" in urlParams && urlParams.slEntryId) {
      const entryId = Number(urlParams.slEntryId);
      if (!isNaN(entryId)) {
        return entryId;
      }
    }
  } else if (pageType === "SLBatch") {
    if ("slBatchRefGUID" in urlParams && urlParams.slBatchRefGUID) {
      return urlParams.slBatchRefGUID;
    }
  } else {
    throw new FatalError("Incorrect type argument used on useDataFromSubledger Hook.");
  }
  return null;
}

async function getPayload(
  pageType: "SLCreate" | "SLBatch",
  entryId: number | string,
  accessToken: IUserProviderState["accessToken"]
) {
  if (pageType === "SLCreate") {
    return getEntryFromApi(entryId as number, accessToken);
  } else if (pageType === "SLBatch") {
    return getBatchEntryFromApi(entryId as string, accessToken);
  } else {
    throw new FatalError("Incorrect type argument used on useDataFromSubledger Hook.");
  }
}

const useDataFromSubledger = (
  pageType: "SLCreate" | "SLBatch"
): [UseDataFromSubledgerState, (entry: ISLEntryResponse | ISLBatchResponse | null) => void] => {
  const { appInsights } = useContext(LoggingContext);
  if (!appInsights) {
    throw new FatalError("Please setup ApplicationInsights Provider for hook useSlEntryFromApi");
  }
  const { accessToken } = useContext(UserContext);
  const urlParams = useParams();
  const history = useHistory();
  const location = useLocation();
  const [dataFetcherType] = useState<"SLCreate" | "SLBatch">(pageType);

  const [slEntryState, dispatch] = useReducer<useSlEntryFromApiStateReducer>(
    (previousState, changes) => ({
      ...previousState,
      ...changes
    }),
    UseSlEntryInitialState
  );

  const setEntry = (entry: ISLEntryResponse | ISLBatchResponse | null) => {
    if (dataFetcherType === "SLCreate") {
      if (entry === null) {
        dispatch({ entry });
      } else {
        const sanitized = sanitizeRegularEntryFromApi(entry as ISLEntryResponse);
        dispatch({
          entry: sanitized
        });
      }
    } else {
      if (entry === null) {
        dispatch({ entry });
      } else {
        const sanitized = sanitizeBatchEntryFromApi(entry as ISLBatchResponse);
        dispatch({
          entry: sanitized
        });
      }
    }
  };

  useEffect(() => {
    return () => {
      dispatch(UseSlEntryInitialState);
    };
  }, []);

  useEffect(() => {
    let requestCanceled = false;
    if (slEntryState.loadingStatus !== SLLoadingStatus.Pending) {
      const entryId = getEntryId(dataFetcherType, urlParams);
      if (entryId !== null) {
        const loadEntry = async (theEntryId: number | string) => {
          try {
            const slEntryFromApi = await getPayload(dataFetcherType, theEntryId, accessToken);
            if (!requestCanceled) {
              dispatch({
                entry: slEntryFromApi,
                loadingStatus: SLLoadingStatus.Resolved
              });
            }
          } catch (e) {
            if (!requestCanceled) {
              console.error(e);
              appInsights.trackException({ exception: e });
              if (e instanceof FatalError) {
                redirectMeToTheOrigin(history, location);
              } else {
                dispatch({
                  notification: {
                    type: "errors",
                    messages: ["Cannot connect to Subledger right now. Please try again later."]
                  },
                  loadingStatus: SLLoadingStatus.Rejected
                });
              }
            }
          }
        };
        dispatch({
          loadingStatus: SLLoadingStatus.Pending
        });
        loadEntry(entryId);
      } else {
        if (!requestCanceled) {
          dispatch({
            entry: null,
            loadingStatus: SLLoadingStatus.Resolved
          });
        }
      }
    }
    return () => {
      requestCanceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return [
    {
      entry: slEntryState.entry,
      notification: slEntryState.notification,
      loadingStatus: slEntryState.loadingStatus,
      dataType: dataFetcherType
    },
    setEntry
  ];
};

export { useDataFromSubledger };
